import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { useService } from '../../hooks/useServices';
import './ServiceDetail.css';

const DEFAULT_IMAGES = {
  reiki: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200&q=80',
  'reiki-intro': 'https://images.unsplash.com/photo-1602192509154-0b900ee1f851?w=1200&q=80',
  'deep-release': 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1200&q=80',
  tarot: 'https://images.unsplash.com/photo-1567016432779-094069958ea5?w=1200&q=80',
  'soul-coaching': 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=1200&q=80',
  chakra: 'https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?w=1200&q=80',
  'space-clearing': 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80',
  'restorative-reiki': 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1200&q=80',
  'guided-ascension': 'https://images.unsplash.com/photo-1528715471579-d1bcf0ba5e83?w=1200&q=80'
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const { service, loading } = useService(slug);

  const getImage = (service) => {
    return service?.image || DEFAULT_IMAGES[service?.slug] || DEFAULT_IMAGES.reiki;
  };

  if (loading) {
    return (
      <div className="service-detail">
        <section className="service-hero">
          <div className="container">
            <div className="service-hero__content">
              <h1>Loading...</h1>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  return (
    <>
      <SEO
        title={`${service.name} Sydney | KO Holistic Health`}
        description={service.description.substring(0, 160)}
        url={`/services/${service.slug}`}
        keywords={`${service.name} sydney, ${service.tagline}, holistic healing sydney, kirstie warburton, energy healing, wellness practitioner sydney`}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Services', path: '/' }, { name: service.name, path: `/services/${service.slug}` }]}
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": service.name,
          "description": service.description,
          "provider": {
            "@type": "Person",
            "name": "Kirstie Warburton",
            "worksFor": { "@type": "HealthAndBeautyBusiness", "name": "KO Holistic Health" }
          },
          "areaServed": { "@type": "City", "name": "Sydney" },
          "offers": typeof service.price === 'number' ? {
            "@type": "Offer",
            "price": service.price,
            "priceCurrency": "AUD"
          } : undefined
        }}
      />

      <div className="service-detail">
        <section className="service-hero">
          <div className="service-hero__image">
            <img
              src={getImage(service)}
              alt={service.name}
              className="service-hero__bg"
            />
            <div className="service-hero__overlay"></div>
          </div>
          <div className="container">
            <div className="service-hero__content">
              <h1>{service.name}</h1>
              <p className="service-hero__tagline">{service.tagline}</p>
              <div className="service-hero__meta">
                <span className="meta-item">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
                  </svg>
                  {service.duration}
                </span>
                <span className="meta-item meta-price">
                  {typeof service.price === 'number' ? `$${service.price} AUD` : service.price}
                </span>
              </div>
              <Button href="/contact" size="large" variant="accent">
                Book This Service
              </Button>
            </div>
          </div>
        </section>

        <section className="section service-description">
          <div className="container">
            <div className="service-layout">
              <div className="service-main">
                <div className="service-section">
                  <h2>What is {service.name}?</h2>
                  <p className="service-intro">{service.description}</p>
                </div>

                {service.benefits && service.benefits.length > 0 && (
                  <div className="service-section">
                    <h2>Benefits</h2>
                    <ul className="benefits-list">
                      {service.benefits.map((benefit, index) => (
                        <li key={index}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                            <polyline points="22 4 12 14.01 9 11.01"/>
                          </svg>
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {service.whatToExpect && service.whatToExpect.length > 0 && (
                  <div className="service-section">
                    <h2>What to Expect</h2>
                    <div className="expect-grid">
                      {service.whatToExpect.map((step, index) => (
                        <div key={index} className="expect-item">
                          <span className="expect-number">{index + 1}</span>
                          <p>{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {service.testimonials && service.testimonials.length > 0 && (
                  <div className="service-section">
                    <h2>Client Experiences</h2>
                    <div className="testimonials-list">
                      {service.testimonials.map((testimonial, index) => (
                        <Card key={index} variant="beige" className="service-testimonial">
                          <p className="testimonial-text">"{testimonial.text}"</p>
                          <p className="testimonial-author">— {testimonial.author}</p>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <aside className="service-sidebar">
                <Card className="booking-card">
                  <h3>Ready to Begin?</h3>
                  <div className="booking-details">
                    <div className="booking-item">
                      <span className="booking-label">Investment</span>
                      <span className="booking-value">
                        {typeof service.price === 'number' ? `$${service.price} AUD` : service.price}
                      </span>
                    </div>
                    <div className="booking-item">
                      <span className="booking-label">Duration</span>
                      <span className="booking-value">{service.duration}</span>
                    </div>
                    <div className="booking-item">
                      <span className="booking-label">Location</span>
                      <span className="booking-value">Sydney or Online</span>
                    </div>
                  </div>
                  <Button href="/contact" fullWidth size="large">
                    Book Now
                  </Button>
                  <p className="booking-note">
                    Not sure if this is right for you? Book a free 15-minute discovery call.
                  </p>
                </Card>

                <div className="faq-section">
                  <h3>Common Questions</h3>
                  <div className="faq-item">
                    <h4>What should I bring?</h4>
                    <p>Just yourself and an open mind. All materials are provided.</p>
                  </div>
                  <div className="faq-item">
                    <h4>What should I wear?</h4>
                    <p>Comfortable, loose-fitting clothing is recommended.</p>
                  </div>
                  <div className="faq-item">
                    <h4>Can I do this online?</h4>
                    <p>Yes! Most services are available both in-person and via Zoom.</p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServiceDetail;
